// Ilham listesinin Etsy verisi: analize gonderildigi gunku deger ile guncel deger
// yan yana. "Guncel veriyi getir" Etsy Open API'den ceker (sayfa kazima yok) ve
// gecmise kaydeder; aradaki gunluk artis listenin GERCEK hizini gosterir.

import { useMutation } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";
import { authHeaders, getBackendUrl } from "../../lib/http";

export interface StatsSnap { at: string; views: number | null; favorites: number | null; ageDays: number | null; state?: string | null }
type Baseline = { views?: number | null; favorites?: number; ageDays?: number; viewsPerDay?: number | null; favPerDay?: number };

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" });
const fmt = (n: number | null | undefined) => (n == null ? "—" : n.toLocaleString("tr-TR"));

export function ListingStatsCompare({ analysisId, capturedAt, baseline, history, onRefreshed }: {
  analysisId: string; capturedAt: string; baseline?: Baseline; history: StatsSnap[]; onRefreshed: () => void;
}) {
  const refresh = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${getBackendUrl()}/api/analyses/${analysisId}/stats-refresh`, { method: "POST", headers: authHeaders() });
      const j = await r.json().catch(() => null);
      if (!r.ok) throw new Error(j?.error ?? `Güncel veri alınamadı (${r.status})`);
    },
    onSuccess: onRefreshed,
  });

  // Referans: analiz anindaki veri; yoksa ilk guncelleme
  const base: StatsSnap | null = baseline && (baseline.views != null || baseline.favorites != null)
    ? { at: capturedAt, views: baseline.views ?? null, favorites: baseline.favorites ?? null, ageDays: baseline.ageDays ?? null }
    : history[0] ?? null;
  const last = history.length && history[history.length - 1] !== base ? history[history.length - 1] : null;
  const days = base && last ? Math.max(0, (new Date(last.at).getTime() - new Date(base.at).getTime()) / 86400000) : 0;
  const rows: Array<{ label: string; key: "views" | "favorites" }> = [{ label: "Görüntülenme", key: "views" }, { label: "Favori", key: "favorites" }];

  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-gray-700">Etsy verisi</p>
        <button onClick={() => refresh.mutate()} disabled={refresh.isPending}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
          {refresh.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Güncel veriyi getir
        </button>
      </div>
      {!base ? (
        <p className="text-xs text-gray-500">Analiz anında Etsy verisi kaydedilmemiş. Güncel veriyi getirince bugünkü değer referans olur, sonraki getirmelerde karşılaştırılır.</p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-gray-500">
              <th className="py-1 text-left font-normal"></th>
              <th className="py-1 text-right font-normal">{base.at === capturedAt ? "Analiz anı" : "İlk kayıt"}<br /><span className="text-gray-400">{fmtDate(base.at)}</span></th>
              {last && <th className="py-1 text-right font-normal">Güncel<br /><span className="text-gray-400">{fmtDate(last.at)}</span></th>}
              {last && <th className="py-1 text-right font-normal">Fark</th>}
            </tr>
          </thead>
          <tbody className="text-gray-900">
            {rows.map(({ label, key }) => {
              const a = base[key], b = last?.[key] ?? null;
              const diff = a != null && b != null ? b - a : null;
              const perDay = diff != null && days >= 1 ? diff / days : null;
              return (
                <tr key={key} className="border-t border-gray-50">
                  <td className="py-1 text-gray-500">{label}</td>
                  <td className="py-1 text-right font-medium">{fmt(a)}</td>
                  {last && <td className="py-1 text-right font-medium">{fmt(b)}</td>}
                  {last && (
                    <td className={`py-1 text-right font-medium ${diff == null ? "text-gray-400" : diff > 0 ? "text-emerald-700" : diff < 0 ? "text-red-600" : "text-gray-500"}`}>
                      {diff == null ? "—" : `${diff > 0 ? "+" : ""}${fmt(diff)}`}
                      {perDay != null && <span className="block text-[11px] font-normal text-gray-500">{perDay.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}/gün</span>}
                    </td>
                  )}
                </tr>
              );
            })}
            <tr className="border-t border-gray-50">
              <td className="py-1 text-gray-500">Liste yaşı</td>
              <td className="py-1 text-right">{base.ageDays != null ? `${base.ageDays} gün` : "—"}</td>
              {last && <td className="py-1 text-right">{last.ageDays != null ? `${last.ageDays} gün` : "—"}</td>}
              {last && <td className="py-1 text-right text-gray-500">{days >= 1 ? `${Math.round(days)} gün geçti` : "aynı gün"}</td>}
            </tr>
          </tbody>
        </table>
      )}
      {last?.state && last.state !== "active" && <p className="mt-1 text-xs text-amber-700">Liste Etsy'de artık aktif değil ({last.state}).</p>}
      {last && days < 1 && <p className="mt-1 text-xs text-gray-500">Günlük artış için en az bir gün sonra yeniden getir.</p>}
      {refresh.error && <p className="mt-1 text-xs text-red-600">{(refresh.error as Error).message}</p>}
    </div>
  );
}
